import { Component, inject, input, signal } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormControl, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { catchError, combineLatest, delay, distinctUntilChanged, map, Observable, of, switchMap, tap } from 'rxjs';
import { FeedbackFailComponent, FeedbackSuccessComponent, FormFieldLoadingComponent } from 'ok-angular-shared/components';
import { G4Service, GetG4AgreementResponse } from '../../services/g4.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'adm-g4-agreement-number',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FeedbackSuccessComponent,
    FeedbackFailComponent,
    FormFieldLoadingComponent,
  ],
  templateUrl: './adm-g4-agreement-number.component.html',
  styleUrl: './adm-g4-agreement-number.component.scss',
})
export class AdmG4AgreementNumberComponent {
  g4Service = inject(G4Service);
  agreementDetails = signal<GetG4AgreementResponse | null>(null);
  control = input.required<FormControl<number | null>>();
  required = input<boolean>(true);
  label = input<string>('G4 Aftalenummer');
  private patternRegex = /^\d{2,12}$/;

  constructor() {
    combineLatest([toObservable(this.control), toObservable(this.required)])
      .pipe(
        tap(([ctrl, isRequired]) => {
          const validators = isRequired
            ? [Validators.required, Validators.pattern(this.patternRegex)]
            : [Validators.pattern(this.patternRegex)];
          ctrl.setValidators(validators);
          ctrl.setAsyncValidators([this.g4AgreementExistsValidator()]);
          ctrl.updateValueAndValidity();
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  private g4AgreementExistsValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value) {
        return of(null);
      }

      return of(control.value).pipe(
        delay(300),
        switchMap(value => this.g4Service.getG4Agreement(value)),
        map(result => {
          if (result) {
            this.agreementDetails.set(result);
            return null;
          }
          this.agreementDetails.set(null);
          return { agreementDoesNotExist: true };
        }),
        catchError(() => {
          this.agreementDetails.set(null);
          return of({ agreementDoesNotExist: true });
        })
      );
    };
  }
}
